import type { BlockNode, BlockRegistry, ShowForRule } from './types';

/** The showFor rule axes, in editor display order. */
export const SHOW_FOR_AXES = ['tags', 'permissions', 'roles'] as const;

/** Whether a node carries any populated conditional-rendering rule. */
export function hasShowForRule(showFor?: ShowForRule): boolean {
  return SHOW_FOR_AXES.some((axis) => (showFor?.[axis]?.length ?? 0) > 0);
}

/**
 * Copy without undefined / empty-array values — stored prop objects and
 * rules stay minimal (an unset field is absent, never `[]`/undefined).
 */
export function withoutEmptyValues<T extends Record<string, unknown>>(
  obj: T,
): Partial<T> {
  const next: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;
    next[key] = value;
  });
  return next as Partial<T>;
}

/** Depth-first search for a node by id. */
export function findBlock(root: BlockNode, id: string): BlockNode | undefined {
  if (root.id === id) return root;
  for (const child of root.children) {
    const found = findBlock(child, id);
    if (found) return found;
  }
  return undefined;
}

/** Immutably replace the node with `id` using `update`. */
export function updateBlock(
  root: BlockNode,
  id: string,
  update: (node: BlockNode) => BlockNode,
): BlockNode {
  if (root.id === id) return update(root);
  return {
    ...root,
    children: root.children.map((c) => updateBlock(c, id, update)),
  };
}

/** Immutably remove the node with `id` from the tree. */
export function removeBlock(root: BlockNode, id: string): BlockNode {
  return {
    ...root,
    children: root.children
      .filter((c) => c.id !== id)
      .map((c) => removeBlock(c, id)),
  };
}

/** Find the direct parent of the node with `id`. */
export function findParentBlock(
  root: BlockNode,
  id: string,
): BlockNode | undefined {
  if (root.children.some((c) => c.id === id)) return root;
  for (const child of root.children) {
    const parent = findParentBlock(child, id);
    if (parent) return parent;
  }
  return undefined;
}

/**
 * Immutably move a child within `parentId`'s children from one index to
 * another (arrayMove semantics — `toIndex` is the destination position).
 */
export function reorderChildren(
  root: BlockNode,
  parentId: string,
  fromIndex: number,
  toIndex: number,
): BlockNode {
  return updateBlock(root, parentId, (n) => {
    const children = [...n.children];
    const [moved] = children.splice(fromIndex, 1);
    if (!moved) return n;
    children.splice(toIndex, 0, moved);
    return { ...n, children };
  });
}

/**
 * Deep-copy a subtree, minting fresh ids (ids must be unique within a slot)
 * and dropping `locked` — a user-made copy is user-owned, not seeded.
 */
export function cloneBlockWithNewIds(node: BlockNode): BlockNode {
  const { locked: _locked, ...rest } = node;
  return {
    ...rest,
    id: crypto.randomUUID(),
    props: { ...node.props },
    children: node.children.map(cloneBlockWithNewIds),
  };
}

/** Immutably insert a copy of the node with `id` directly after it. */
export function duplicateBlock(root: BlockNode, id: string): BlockNode {
  const parent = findParentBlock(root, id);
  if (!parent) return root;
  return updateBlock(root, parent.id, (n) => {
    const index = n.children.findIndex((c) => c.id === id);
    if (index === -1) return n;
    const children = [...n.children];
    children.splice(index + 1, 0, cloneBlockWithNewIds(n.children[index]));
    return { ...n, children };
  });
}

/** Immutably insert `block` into `parentId`'s children at `index`. */
export function insertChild(
  root: BlockNode,
  parentId: string,
  index: number,
  block: BlockNode,
): BlockNode {
  return updateBlock(root, parentId, (n) => {
    const children = [...n.children];
    children.splice(index, 0, block);
    return { ...n, children };
  });
}

/** Count every node under (and excluding) the root region. */
export function countBlocks(root: BlockNode): number {
  return root.children.reduce((n, c) => n + 1 + countBlocks(c), 0);
}

/**
 * Admin-facing label for a chip: the custom layer name first (node.name,
 * identity metadata à la Figma), then authored text (`title`/`content`),
 * then the registry display name, then the raw type key.
 */
export function getBlockLabel(
  node: BlockNode,
  registry: BlockRegistry,
): string {
  if (node.name?.trim()) return node.name;
  const { title, content } = node.props as {
    title?: unknown;
    content?: unknown;
  };
  if (typeof title === 'string' && title.trim()) return title;
  if (typeof content === 'string' && content.trim()) return content;
  return registry[node.type]?.displayName ?? node.type;
}

/** A container is a block whose definition allows children. */
export function isContainer(node: BlockNode, registry: BlockRegistry): boolean {
  return Boolean(registry[node.type]?.allowedChildren?.length);
}

/**
 * Block types a user may add inside a container: the container's
 * `allowedChildren`, minus seeded-only (`placeable: false`) types.
 */
export function getPlaceableChildren(
  containerType: string,
  registry: BlockRegistry,
): string[] {
  return (registry[containerType]?.allowedChildren ?? []).filter(
    (type) => registry[type]?.placeable !== false,
  );
}

/** Mint a new block of `type` with a fresh id and schema-default props. */
export function createBlock(type: string, registry: BlockRegistry): BlockNode {
  const def = registry[type];
  const props: Record<string, unknown> = {};
  Object.entries(def?.schema.properties ?? {}).forEach(([name, property]) => {
    if (property.default !== undefined) props[name] = property.default;
  });
  return {
    id: crypto.randomUUID(),
    type,
    props,
    children: [],
  };
}
